import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { AuthContext } from "src/contexts/AuthContext";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import AlenProductConfig from "src/services/alen/alenDeviceConfig";
import {
  findProductFunctions,
  flattenProductConfig,
  getAlenProductFunctions,
} from "src/services/alen/alenDeviceConfig/utils";
import {
  DeviceActionPayload,
  performDeviceActions,
} from "src/services/alen/performDeviceActions";
import { AlenDeviceImage } from "./AlenDeviceCard";

export default function AlenIndividualActionModal({
  device,
  open,
  onClose = () => {},
  onActionPerformed,
  selected,
  standardName,
}: {
  device: AlenDevice;
  open: boolean;
  onClose?: () => void;
  onActionPerformed: () => void;
  selected: string;
  standardName: string;
}) {
  const [inputValue, setInputValue] = useState(selected);
  const { user } = useContext(AuthContext);
  const standardFunction = findProductFunctions(
    getAlenProductFunctions(device.product_id, AlenProductConfig),
    standardName
  );
  const handleExecute = async () => {
    if (!user?.alen) return;
    if (!standardFunction.standardValues) return;
    const payload: DeviceActionPayload = {
      actionName: standardFunction.standardName,
      actionValue: inputValue,
      actionType: "Individual",
      deviceDetails: [
        {
          productId: device.product_id,
          deviceIds: [device.id],
        },
      ],
    };
    try {
      await performDeviceActions(user.alen, payload);
      onActionPerformed();
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
  const iconUrl = flattenProductConfig(AlenProductConfig).find(
    (c) => c.productInfo.pid === device.product_id
  )?.productInfo.image;
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent
        sx={{
          gap: 2,
          display: "flex",
          flexDirection: "column",
          minWidth: "20rem",
        }}
      >
        <Stack direction="row" gap={1} height={48} alignItems="center">
          {iconUrl && <AlenDeviceImage url={iconUrl} />}
          <Stack direction="column" gap={1}>
            <Typography variant="h2" marginBottom="0">
              {device.name}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {device.id}
            </Typography>
          </Stack>
        </Stack>
        <Typography fontWeight="600">
          {standardFunction.standardName}
        </Typography>
        <Stack direction="row" gap={1}>
          {standardFunction.standardValues?.map((sValue) => {
            const value = Object.keys(sValue).map((k) => k)[0];
            const isSelected = inputValue === value;
            return (
              <Button
                sx={{ flex: 1 }}
                onClick={() => setInputValue(value)}
                variant={isSelected ? "contained" : "outlined"}
                key={value}
              >
                {value}
              </Button>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="danger" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleExecute}>
          Execute
        </Button>
      </DialogActions>
    </Dialog>
  );
}
